/**
 * SCOPE OWNERSHIP AND ATTRIBUTED EXECUTION — presentation for
 * `provider-outcome-2`.
 *
 * The question this surface answers: **what actually happened on scope this
 * provider owned?** And the one it refuses: **did the provider cause any of
 * it?**
 *
 * Ownership is the only bridge from an execution actual to a firm. No
 * execution table carries a provider column, and by contract none will —
 * attribution flows through recorded ownership windows or not at all. So
 * every fact on this surface carries three things and this module renders
 * all three, never one without the others:
 *
 *   FACT CLASS         DERIVED_FACT | HUMAN_ASSERTED | UNKNOWN | CONFLICT
 *   ATTRIBUTION BASIS  AUTHORIZED_COMMERCIAL_SELECTION | NAMED_HUMAN_ASSIGNMENT | UNKNOWN
 *   CAUSALITY          NOT_ESTABLISHED, permanently
 *
 * Four rules are load-bearing, and each has a test:
 *
 *  1. **Ownership is not causality.** A window says who held the scope on a
 *     date. It never says who made a thing happen. No copy on this surface
 *     may say because / caused / led to / due to / resulted in, and nothing
 *     draws an arrow between two records.
 *  2. **No delta, ever.** The daily pulse carries planned and actual
 *     manpower. Subtracting them would manufacture a per-provider staffing
 *     shortfall series — a performance verdict wearing arithmetic. The
 *     planned figure is the tenant's own plan; this module reports both
 *     numbers side by side and computes nothing from them.
 *  3. **Absence is the tenant's recording posture, never the provider's
 *     silence.** No tie, no trail, no fact — each is a gap in what this
 *     tenant wrote down.
 *  4. **A disputed fact attributes to NOBODY.** Two firms on one package,
 *     or one instance reaching two firms' packages: the facts are
 *     enumerated and attributed to no one. Appearing in this firm's payload
 *     is enumeration, never a claim against them.
 */

export const PROVIDER_OUTCOME_CONTRACT = 'provider-outcome-2';

/**
 * Words this surface may never put next to a provider. The first group is
 * causal (the contract forbids the causal reading outright); the second is
 * evaluative (no score, rating, ranking or grade exists in the payload and
 * none may be computed from it); the third is verdict language about
 * schedule, which is a finding this platform does not make.
 *
 * A test walks every string this module can emit against this list.
 */
export const FORBIDDEN_OUTCOME_WORDS = {
  causal: ['because', 'caused', 'led to', 'due to', 'resulted in', 'impact of'],
  evaluative: ['score', 'rating', 'ranking', 'grade', 'performance', 'reliability'],
  verdict: ['late', 'delay', 'behind schedule', 'shortfall', 'underperform', 'fault', 'blame'],
};

/**
 * The absence word the contract emits so it can say it is never emitted.
 * If this string ever reaches a rendered state, the surface is wrong.
 */
export const NEVER_EMITTED_ABSENCE = 'NOT_CONSIDERED';

/* -------------------------------------------------------------------------
 * FACT CLASS — four, and none of them is a quality judgement
 * ---------------------------------------------------------------------- */

const FACT_CLASSES = {
  DERIVED_FACT: {
    key: 'DERIVED_FACT',
    label: 'Composed now',
    tone: 'derived',
    meaning: 'Arithmetic over recorded values. No row states this directly.',
  },
  HUMAN_ASSERTED: {
    key: 'HUMAN_ASSERTED',
    label: 'A person recorded this',
    tone: 'asserted',
    meaning: 'A governed observation stands behind this value, with its own date and actor.',
  },
  UNKNOWN: {
    key: 'UNKNOWN',
    label: 'No trail reaches this',
    tone: 'unknown',
    meaning:
      'The column holds a value and no observation trail explains it. It is echoed as recorded — never promoted to anyone’s assertion, and never dropped.',
  },
  CONFLICT: {
    key: 'CONFLICT',
    label: 'Two standing answers',
    tone: 'conflict',
    meaning:
      'The column and the latest assertion disagree. Both are shown; nothing here picks between them.',
  },
};

/**
 * UNKNOWN is not a failing grade and CONFLICT is not an error state. Their
 * tones are their own — a test asserts neither reads as `good` or `bad`,
 * because a colour that means "this provider did badly" is exactly the
 * inference this payload refuses to support.
 */
export const factClassDisplay = (factClass) =>
  FACT_CLASSES[factClass] || {
    key: String(factClass ?? 'UNKNOWN'),
    label: 'Not a class this view knows',
    tone: 'unknown',
    meaning:
      'The server named a fact class this view does not carry. It is shown verbatim rather than mapped onto one that looks close.',
  };

/* -------------------------------------------------------------------------
 * ATTRIBUTION BASIS — how the fact reached this firm at all
 * ---------------------------------------------------------------------- */

const ATTRIBUTION_BASES = {
  AUTHORIZED_COMMERCIAL_SELECTION: {
    key: 'AUTHORIZED_COMMERCIAL_SELECTION',
    label: 'Through an authorized award',
    strength: 'scope-level',
    meaning:
      'A machine-written ownership window, taken from an authorized selection’s own quote references. The strongest tie this platform has to a scope.',
  },
  NAMED_HUMAN_ASSIGNMENT: {
    key: 'NAMED_HUMAN_ASSIGNMENT',
    label: 'A person assigned this scope',
    strength: 'human-asserted',
    meaning:
      'Someone in your company recorded that this firm holds this scope. It stays asserted — it is never promoted to a commercial award.',
  },
  UNKNOWN: {
    key: 'UNKNOWN',
    label: 'Attributed to nobody',
    strength: 'none',
    meaning:
      'No ownership window covers this fact’s own date, or more than one claim reaches it. The fact is listed and attached to no firm.',
  },
};

export const attributionBasisDisplay = (basis) =>
  ATTRIBUTION_BASES[basis] || ATTRIBUTION_BASES.UNKNOWN;

/* -------------------------------------------------------------------------
 * THE TIE — which canonical column reached from package grain to work
 * ---------------------------------------------------------------------- */

const LINKAGES = {
  REQUIREMENT_LINKAGE: {
    key: 'REQUIREMENT_LINKAGE',
    label: 'A requirement naming both the package and this work face',
    weak: false,
  },
  MATERIAL_ANCHOR: {
    key: 'MATERIAL_ANCHOR',
    label: 'A material anchored to a line on this package',
    weak: false,
  },
  REQUIREMENT_LINKAGE_ACTIVITY_GRAIN: {
    key: 'REQUIREMENT_LINKAGE_ACTIVITY_GRAIN',
    label: 'A requirement reaching the planning activity, not this work face',
    weak: true,
  },
  PROCUREMENT_ACTIVITY: {
    key: 'PROCUREMENT_ACTIVITY',
    label: 'A procurement line reaching the planning activity, not this work face',
    weak: true,
  },
};

export const WEAK_TIE_CAVEAT =
  'Every tie here reaches a planning activity rather than a work face, so many instances can share one tie. Read the attachment as broad, not exact.';

export const linkageDisplay = (basis) =>
  LINKAGES[basis] || {
    key: String(basis ?? 'UNKNOWN'),
    label: 'A linkage this view does not name',
    weak: true,
  };

/* -------------------------------------------------------------------------
 * OWNERSHIP WINDOWS
 * ---------------------------------------------------------------------- */

const day = (value) => {
  if (!value) return null;
  const text = String(value);
  return text.length >= 10 ? text.slice(0, 10) : text;
};

const OWNERSHIP_BASES = {
  AUTHORIZED_COMMERCIAL_SELECTION: 'Written from an authorized award',
  NAMED_HUMAN_ASSIGNMENT: 'Recorded by a person',
};

export const RETROACTIVE_CAVEAT =
  'This window was asserted after the period it claims. Whatever rests on it rests on a backdated assertion.';

export const DANGLING_CAVEAT =
  'The record this window names no longer resolves in this tenant. The window stands as recorded and matches no live record.';

export const FINER_REFS_CAVEAT =
  'Activity, line and requirement references are echoed from the record and ground nothing — ownership is read at package grain only.';

/**
 * One recorded ownership window.
 *
 * `open` is a fact about the window, not a status: a closed window keeps
 * grounding everything it grounded while it was open. The governed close
 * ends the window, never the record — so a closed window is presented as
 * *ended*, never as *revoked* or *lost*.
 */
export const presentWindow = (assignment) => {
  if (!assignment) return null;
  const from = day(assignment.effectiveFrom);
  const to = day(assignment.effectiveTo);
  const finer = [
    assignment.activityId ? { kind: 'Activity', id: assignment.activityId } : null,
    assignment.procurementItemId
      ? { kind: 'Procurement line', id: assignment.procurementItemId }
      : null,
    assignment.requirementId ? { kind: 'Requirement', id: assignment.requirementId } : null,
  ].filter(Boolean);
  return {
    id: assignment.id,
    projectId: assignment.projectId || null,
    packageId: assignment.bidPackageId || null,
    recordType: assignment.recordType || null,
    recordId: assignment.recordId || null,
    scopeRole: assignment.scopeRole || null,
    basis: assignment.basis || null,
    basisLabel: OWNERSHIP_BASES[assignment.basis] || 'Basis not named by the server',
    attribution: attributionBasisDisplay(assignment.basis),
    open: assignment.current === true,
    // "Ended", never "revoked": the close is governed and the ownership
    // that held during the window is untouched by it.
    windowText: to ? `${from || 'start not recorded'} → ${to}` : `${from || 'start not recorded'} → open`,
    effectiveFrom: from,
    effectiveTo: to,
    endedReason: assignment.endedReason || null,
    authorizedBy: assignment.authorizedBy || null,
    authorizedAt: assignment.authorizedAt || null,
    closedBy: assignment.closedBy || null,
    closedAt: assignment.closedAt || null,
    retroactive: assignment.retroactive === true,
    retroactiveCaveat: assignment.retroactive === true ? RETROACTIVE_CAVEAT : null,
    dangling: assignment.danglingRecord === true,
    danglingCaveat: assignment.danglingRecord === true ? DANGLING_CAVEAT : null,
    finerRefs: finer,
    finerRefsCaveat: finer.length ? FINER_REFS_CAVEAT : null,
  };
};

const OWNERSHIP_CONFLICT_KINDS = {
  MULTI_ASSIGNMENT: {
    key: 'MULTI_ASSIGNMENT',
    label: 'More than one firm holds this package right now',
    meaning:
      'Two or more current windows claim the same package. Both stand; this view picks no winner and neither does the record.',
  },
  MACHINE_HUMAN_DIVERGENCE: {
    key: 'MACHINE_HUMAN_DIVERGENCE',
    label: 'An award and a person name different firms',
    meaning:
      'A window written from an authorized award and a window recorded by a person disagree. Both are shown as recorded.',
  },
  SAME_DAY_WINDOWS: {
    key: 'SAME_DAY_WINDOWS',
    label: 'Two windows of this firm cover the same days',
    meaning:
      'The same firm, package and role appear in two overlapping windows — the close-and-recreate shape. The authorization stamps are shown as recorded evidence only; nothing here picks between them.',
  },
};

export const ownershipConflictDisplay = (kind) =>
  OWNERSHIP_CONFLICT_KINDS[kind] || {
    key: String(kind ?? 'UNKNOWN'),
    label: 'A disagreement this view does not name',
    meaning: 'The server named a conflict kind this view does not carry. It is shown verbatim.',
  };

const OWNERSHIP_UNKNOWN_KINDS = {
  AUTHORIZED_NO_OWNERSHIP_ROW: {
    key: 'AUTHORIZED_NO_OWNERSHIP_ROW',
    label: 'Authorized to proceed, with no ownership window recorded',
    meaning:
      'This firm was authorized on the package and no window, open or ended, names any of its records. The write surface may have been off, a backfill may have skipped it, or the record may predate the feature.',
  },
};

export const ownershipUnknownDisplay = (kind) =>
  OWNERSHIP_UNKNOWN_KINDS[kind] || {
    key: String(kind ?? 'UNKNOWN'),
    label: 'A gap this view does not name',
    meaning: 'The server named a kind this view does not carry. It is shown verbatim.',
  };

/**
 * Per-package ownership has exactly three shapes, and the absent key is a
 * fourth thing entirely. Collapsing "no rows at all" into "UNKNOWN" would
 * turn a package nobody has recorded ownership for into a package whose
 * ownership was investigated and could not be determined.
 */
export const presentPackageOwnership = (entry) => {
  if (entry === undefined || entry === null) {
    return {
      shape: 'NO_ROWS',
      headline: 'No ownership recorded for this package',
      detail:
        'No window names this firm on this package. That is what this tenant has written down, not a statement about the provider.',
      candidates: [],
      sources: [],
    };
  }
  if (entry.value === 'CONFLICT') {
    return {
      shape: 'CONFLICT',
      headline: 'More than one firm currently claims this package',
      detail:
        'Each claim is listed with the window it comes from. There is no winner, and this view does not choose one.',
      candidates: entry.candidates || [],
      sources: [],
      winner: null,
    };
  }
  if (entry.value === 'UNKNOWN') {
    return {
      shape: 'ENDED',
      headline: 'Every recorded window on this package has ended',
      detail:
        entry.note ||
        'Ownership was recorded and every window is closed. The ended windows stay listed and keep grounding what they grounded.',
      candidates: [],
      sources: entry.sources || [],
    };
  }
  return {
    shape: 'HELD',
    headline: 'Held under a current window',
    detail: null,
    holder: entry.value || null,
    attribution: entry.attribution || null,
    candidates: [],
    sources: entry.sources || [],
  };
};

export const presentScopeOwnership = (family) => {
  if (!family) return null;
  const windows = (family.assignments || []).map(presentWindow).filter(Boolean);
  const byPackage = family.ownershipByPackage || {};
  return {
    basis: family.basis || null,
    causality: family.causality || 'NOT_ESTABLISHED',
    windows,
    windowsTruncated: family.assignmentsTruncated === true,
    hasWindows: windows.length > 0,
    packages: Object.keys(byPackage)
      .sort()
      .map((packageId) => ({
        packageId,
        ...presentPackageOwnership(byPackage[packageId]),
      })),
    conflicts: (family.conflicts || []).map((conflict) => ({
      ...conflict,
      display: ownershipConflictDisplay(conflict.kind),
    })),
    unknowns: (family.unknowns || []).map((unknown) => ({
      ...unknown,
      display: ownershipUnknownDisplay(unknown.kind),
    })),
    attributionGrounds: Object.entries(family.attributionGrounds || {}).map(
      ([basis, strength]) => ({
        basis,
        label: attributionBasisDisplay(basis).label,
        strength,
      })
    ),
  };
};

/* -------------------------------------------------------------------------
 * ATTRIBUTED EXECUTION
 * ---------------------------------------------------------------------- */

const FACT_FIELD_LABELS = {
  actualStart: 'Recorded start',
  actualFinish: 'Recorded finish',
  percentComplete: 'Percent complete as recorded',
  actualManpower: 'Manpower recorded on the instance',
  verified: 'A verification was recorded',
  quantitySufficient: 'Quantity sufficient, as attested',
  delivery: 'Arrival against this tenant’s projected date',
};

export const DISPUTED_FACT_NOTE =
  'More than one claim reaches this work, so this fact is attributed to nobody. It appears in this firm’s ledger for one reason — it touches scope this firm is named on — and that is enumeration, not a claim against them.';

export const RETROACTIVE_FACT_NOTE =
  'This fact predates the authorization of a window grounding it. That much of the attribution rests on a backdated assertion.';

/**
 * The delivery comparison, and the sentence that keeps it honest.
 *
 * `deliveredAfterCurrentPlan` is arithmetic over two recorded dates. The
 * expected date is the TENANT's own procurement projection — not a date
 * this provider promised — so the comparison is stated as a relation
 * between two dates and never as lateness, and `isProviderDelay` stays
 * NOT_ESTABLISHED beside it.
 */
export const presentDelivery = (value) => {
  if (!value || typeof value !== 'object') return null;
  const after = value.deliveredAfterCurrentPlan === true;
  return {
    expected: day(value.expectedDeliveryDate),
    actual: day(value.actualDeliveryDate),
    after,
    // Deliberately not "late": the projected date is this tenant's own.
    relation: after
      ? 'Arrived after the date this tenant projected'
      : 'Arrived on or before the date this tenant projected',
    projectionNote:
      'The projected date is this tenant’s own procurement projection, not a date this provider committed to.',
    isProviderDelay: value.isProviderDelay || 'NOT_ESTABLISHED',
  };
};

/** One classed fact, with everything needed to read it responsibly. */
export const presentFact = (fact) => {
  if (!fact) return null;
  const basis = attributionBasisDisplay(fact.attributionBasis);
  return {
    field: fact.field,
    label: FACT_FIELD_LABELS[fact.field] || fact.field,
    value: fact.field === 'delivery' ? presentDelivery(fact.value) : fact.value,
    isDelivery: fact.field === 'delivery',
    factClass: factClassDisplay(fact.factClass),
    basis,
    attribution: fact.attribution || 'none',
    causality: fact.causality || 'NOT_ESTABLISHED',
    sources: fact.sources || [],
    windows: fact.windows || [],
    // A fact can carry BOTH — a note about the value and a separate note
    // about why it attributes where it does. Collapsing them into one key
    // silently drops whichever loses, and the attribution note is usually
    // the one that explains a `none`.
    note: fact.note || null,
    attributionNote: fact.attributionNote || null,
    disputed: fact.attributionDisputed === true || Array.isArray(fact.multiPackageTie),
    disputedNote:
      fact.attributionDisputed === true || Array.isArray(fact.multiPackageTie)
        ? DISPUTED_FACT_NOTE
        : null,
    disputedPackages: fact.disputedPackages || fact.multiPackageTie || [],
    retroactive: fact.attributionRetroactive === true,
    retroactiveNote:
      fact.attributionRetroactive === true
        ? fact.retroactiveNote || RETROACTIVE_FACT_NOTE
        : null,
    retroactiveWindows: fact.retroactiveWindows || [],
    alsoAssertedByHumanWindow: fact.alsoAssertedByHumanWindow || [],
    candidates: fact.candidates || null,
    winner: null,
  };
};

export const MANPOWER_NO_DELTA_NOTE =
  'Planned and actual are reported side by side. This view does not subtract one from the other: the plan is your company’s, and the difference between them is a judgement no record here supports.';

/**
 * One day of the pulse. Note what this returns and what it does not: there
 * is no `delta`, no `variance`, and no `shortfall` key anywhere in it, and
 * a test asserts their absence.
 */
export const presentManpowerDay = (entry) => {
  if (!entry) return null;
  const basis = attributionBasisDisplay(entry.attributionBasis);
  const today = entry.percentCompleteToday;
  return {
    id: entry.recordId || entry.id || null,
    logDate: day(entry.logDate),
    plannedManpower: entry.plannedManpower ?? null,
    actualManpower: entry.actualManpower ?? null,
    factClass: factClassDisplay(entry.factClass),
    basis,
    causality: entry.causality || 'NOT_ESTABLISHED',
    disputed: entry.attributionDisputed === true || Array.isArray(entry.multiPackageTie),
    retroactive: entry.attributionRetroactive === true,
    note: entry.attributionNote || null,
    percentCompleteToday: today
      ? {
          value: today.value,
          factClass: factClassDisplay(today.factClass),
          note: today.note || null,
        }
      : null,
  };
};

export const presentConstraintObservation = (entry) => {
  if (!entry) return null;
  return {
    id: entry.recordId || entry.id || null,
    constraintKey: entry.constraintKey || null,
    observationType: entry.observationType || null,
    observedAt: entry.observedAt || null,
    cleared: entry.observationType === 'CONSTRAINT_CLEARED',
    factClass: factClassDisplay(entry.factClass),
    basis: attributionBasisDisplay(entry.attributionBasis),
    disputed: entry.attributionDisputed === true || Array.isArray(entry.multiPackageTie),
    note: entry.attributionNote || null,
  };
};

export const REFUSED_OBSERVATION_NOTE =
  'The machine declined to apply this record. What the person recorded stands; it grounds nothing here, and neither position erases the other.';

export const presentRefusedObservation = (entry) => {
  if (!entry) return null;
  return {
    id: entry.recordId || entry.id || null,
    observationType: entry.observationType || null,
    observedAt: entry.observedAt || null,
    projectionDetail: entry.projectionDetail || null,
    // The server's own sentence, kept rather than overwritten. Ours is the
    // standing rule and is shown once for the section.
    note: entry.note || REFUSED_OBSERVATION_NOTE,
  };
};

export const presentMaterialBlock = (block) => {
  if (!block) return null;
  return {
    id: block.recordId || block.id || null,
    materialName: block.materialName || null,
    facts: (block.facts || []).map(presentFact).filter(Boolean),
  };
};

export const presentInstance = (block) => {
  if (!block) return null;
  const ties = (block.ties || []).map((tie) => ({
    packageId: tie.packageId,
    linkage: linkageDisplay(tie.linkageBasis),
    source: tie.source || null,
    note: tie.linkageNote || null,
  }));
  const facts = (block.facts || []).map(presentFact).filter(Boolean);
  const days = (block.manpowerDays || []).map(presentManpowerDay).filter(Boolean);
  const materials = (block.materials || []).map(presentMaterialBlock).filter(Boolean);
  const constraints = (block.constraints || [])
    .map(presentConstraintObservation)
    .filter(Boolean);
  const refused = (block.refusedObservations || [])
    .map(presentRefusedObservation)
    .filter(Boolean);
  return {
    id: block.recordId || block.id || null,
    projectId: block.projectId || null,
    instanceName: block.instanceName || null,
    ties,
    tieCount: block.tieCount ?? ties.length,
    tiesTruncated: block.tiesTruncated === true,
    weakTieOnly: block.weakTieOnly === true,
    weakTieCaveat: block.weakTieOnly === true ? WEAK_TIE_CAVEAT : null,
    facts,
    manpowerDays: days,
    manpowerDayCount: block.manpowerDayCount ?? days.length,
    manpowerDaysTruncated: block.manpowerDaysTruncated === true,
    manpowerNote: days.length ? MANPOWER_NO_DELTA_NOTE : null,
    materials,
    // No server-side material count exists, so none is invented: falling back
    // to the listed length would print a total that is really a floor.
    materialsTruncated: block.materialsTruncated === true,
    constraints,
    constraintsTruncated: block.constraintsTruncated === true,
    refusedObservations: refused,
    refusedCount: block.refusedCount ?? refused.length,
    refusedObservationsTruncated: block.refusedObservationsTruncated === true,
    // A tied instance with nothing on it is a recording gap, not an idle
    // provider. The panel says which.
    empty:
      facts.length === 0 &&
      days.length === 0 &&
      materials.length === 0 &&
      constraints.length === 0 &&
      refused.length === 0,
  };
};

export const DISPUTE_SCAN_TRUNCATED_NOTE =
  'The scan for competing claims was cut short, so “no other firm claims this” is not proven for every package here. The disputes that were seen still mark their facts.';

export const presentAttributedExecution = (family) => {
  if (!family) return null;
  const instances = (family.instances || []).map(presentInstance).filter(Boolean);
  return {
    basis: family.basis || null,
    causality: family.causality || 'NOT_ESTABLISHED',
    note: family.note || null,
    instances,
    instanceCount: family.instanceCount ?? instances.length,
    instancesTruncated: family.instancesTruncated === true,
    ownedPackageCount: family.ownedPackageCount ?? 0,
    disputedPackages: family.disputedPackages || [],
    disputeScanTruncated: family.disputeScanTruncated === true,
    disputeScanNote: family.disputeScanTruncated === true ? DISPUTE_SCAN_TRUNCATED_NOTE : null,
    factClasses: family.factClasses || [],
    attributionBases: family.attributionBases || [],
    linkageBases: family.linkageBases || [],
    neverEmitted: family.neverEmitted || [],
    // Counts across the listed instances, so the header can say how much is
    // classed which way without the reader opening every row. These count
    // FACTS, never providers, and nothing is ranked by them.
    tally: instances.reduce(
      (acc, instance) => {
        instance.facts.forEach((fact) => {
          acc[fact.factClass.key] = (acc[fact.factClass.key] || 0) + 1;
          if (fact.disputed) acc.attributedToNobody += 1;
        });
        return acc;
      },
      { DERIVED_FACT: 0, HUMAN_ASSERTED: 0, UNKNOWN: 0, CONFLICT: 0, attributedToNobody: 0 }
    ),
  };
};

/* -------------------------------------------------------------------------
 * THE ENVELOPE — the guards that must hold before anything is drawn
 * ---------------------------------------------------------------------- */

export const SCORE_REFUSED_NOTE =
  'This payload carries a value where the contract states there is none. It is not shown, and nothing on this surface is ordered by it.';

export const CAUSALITY_STATEMENT =
  'OMNΛS does not establish cause here, and will not. Nothing in these records ties one event to another as its reason: no execution row names a provider, and no causal vocabulary exists to connect them. The records below are ordered by date and joined by nothing. The server’s own three reasons are listed beside this.';

/**
 * Envelope guards, checked before a single fact is drawn.
 *
 * `scoreWithheld` is the interesting one. The contract says `score` is null
 * on every response. If a value ever arrives there, the honest move is not
 * to render it "because the server sent it" — it is to say a number arrived
 * where the contract says none exists, and show nothing ordered by it.
 */
export const presentOutcomeEnvelope = (payload) => {
  if (!payload) return null;
  const scorePresent = payload.score !== null && payload.score !== undefined;
  return {
    contractVersion: payload.contractVersion || null,
    organizationId: payload.networkOrganizationId || null,
    causality: payload.causality?.posture || 'NOT_ESTABLISHED',
    causalityPermanent: payload.causality?.permanent === true,
    causalityBasis: payload.causality?.basis || [],
    causalityStatement: CAUSALITY_STATEMENT,
    ceiling: payload.attributionCeiling?.statement || null,
    ceilingReached: payload.attributionCeiling?.reached === true,
    scoreNote: payload.scoreNote || null,
    scoreWithheld: scorePresent,
    scoreWithheldNote: scorePresent ? SCORE_REFUSED_NOTE : null,
    readOnly: payload.readOnly === true,
    privacy: payload.privacy || null,
    missingInformation: payload.missingInformation || [],
    scanTruncated: payload.scanTruncated || [],
    conflicts: payload.conflicts || [],
  };
};

/**
 * The refusals, in the order the server applies them. The flag is checked
 * inside the handler and answers identically for a known organization, an
 * unknown one, and another tenant's — so a 404 here means "no identity link
 * in this tenant", which is a route to identity resolution, never an empty
 * ledger.
 */
export const presentOutcomeAvailability = (error) => {
  if (!error) return null;
  switch (error.kind) {
    case 'disabled':
      return {
        kind: 'disabled',
        headline: 'The provider ledger is not enabled in this environment.',
        detail: 'Nothing about which organizations exist is revealed in this state.',
        routeToIdentity: false,
      };
    case 'not-entitled':
      return {
        kind: 'not-entitled',
        headline: 'The provider ledger is not part of your plan.',
        detail: null,
        routeToIdentity: false,
      };
    case 'no-access':
      return {
        kind: 'no-access',
        headline: 'Your role does not include the provider ledger.',
        detail: null,
        routeToIdentity: false,
      };
    case 'not-found':
      return {
        kind: 'not-found',
        headline: 'This tenant holds no identity link for this organization.',
        // The distinction that matters: unavailable, not empty.
        detail:
          'The ledger is unavailable rather than empty. Confirm an identity link and it becomes readable.',
        routeToIdentity: true,
      };
    default:
      return {
        kind: 'error',
        headline: 'The scope and execution ledger did not load.',
        detail: 'The identity facts above are unaffected.',
        routeToIdentity: false,
      };
  }
};

/* -------------------------------------------------------------------------
 * THE COPY THIS MODULE AUTHORS
 *
 * Every user-visible sentence this surface writes itself lives here or in
 * the constants above, and the panel imports its headings from this object
 * rather than inlining them. That is what makes the forbidden-word guard
 * real: a test walks this registry, so a verdict word cannot be smuggled in
 * through a heading typed directly into JSX.
 *
 * Server-authored text (a fact's `note`, a conflict's `detail`, the
 * causality basis) is echoed verbatim and is NOT in this registry. We do
 * not rewrite the server's own words to fit our vocabulary — we only make
 * sure we never add a verdict of our own.
 * ---------------------------------------------------------------------- */

export const OUTCOME_COPY = {
  sectionTitle: 'Scope owned, and what was observed on it',
  sectionLead:
    'Ownership is the only bridge from an execution record to a firm. No execution table names a provider, so every fact below reached this firm through a recorded ownership window — or reached nobody.',
  ownershipTitle: 'Ownership windows',
  ownershipLead:
    'Who held which package, over which days, on whose authority. A window that has ended keeps grounding everything it grounded while it was open.',
  ownershipEmpty:
    'No ownership window names this firm. The write surface carries its own switch and depends on confirmed identity, so an empty set is this tenant’s recording posture — never this provider’s silence.',
  packagesTitle: 'Ownership by package',
  disputesTitle: 'Disagreements, kept side by side',
  disputesLead: 'Every one of these stands unresolved on purpose. Nothing here picks a winner.',
  gapsTitle: 'Named gaps',
  executionTitle: 'Observed on owned scope',
  executionLead:
    'Execution records reached through a canonical linkage column from a package this firm owned on the record’s own date. Each carries its class, its basis, and its evidence.',
  executionEmpty:
    'No recorded ownership window names this firm, so no execution record attaches to it here. That is this tenant’s recording posture, not a statement about the provider.',
  instanceEmpty:
    'This work face is tied to owned scope and carries no governed observation and no recorded actual. Nothing was written down — which is not the same as nothing happening.',
  tiesTitle: 'How this reached the firm',
  factsTitle: 'Facts',
  pulseTitle: 'Daily manpower, planned beside actual',
  materialsTitle: 'Materials anchored to owned scope',
  constraintsTitle: 'Constraints recorded on this work',
  constraintsNote: 'Recorded, not enforced. These gate nothing on their own.',
  refusedTitle: 'Records the machine declined to apply',
  neverEmittedTitle: 'What this view will not tell you',
  provenanceTitle: 'What this ledger can and cannot attach to a firm',
  missingTitle: 'What this read could not establish',
  truncatedTitle: 'Scans that were cut short',
  truncatedLead:
    'Counts drawn from a cut scan are floors, not totals, and an absence drawn from one is unknown rather than proven.',
  listCutShort:
    'This list is cut short. The counts beside it are floors, not totals, and nothing here was dropped quietly.',
  refusedStanding:
    'What a person recorded stands. OMNΛS declined to apply it and said why. Both positions are kept; neither erases the other, and a declined record grounds nothing here.',
  retroactiveLabel: 'Asserted after the fact',
  disputedLabel: 'Attributed to nobody',
  weakTieLabel: 'Broad tie',
  endedLabel: 'Ended',
  openLabel: 'Open',
};

/**
 * The registry the guard walks: every sentence and label this module wrote
 * itself. Server text is deliberately excluded (see above).
 */
export const authoredCopy = () => [
  ...Object.values(OUTCOME_COPY),
  ...Object.values(FACT_CLASSES).flatMap((entry) => [entry.label, entry.meaning]),
  ...Object.values(ATTRIBUTION_BASES).flatMap((entry) => [entry.label, entry.meaning]),
  ...Object.values(LINKAGES).map((entry) => entry.label),
  ...Object.values(OWNERSHIP_BASES),
  ...Object.values(OWNERSHIP_CONFLICT_KINDS).flatMap((entry) => [entry.label, entry.meaning]),
  ...Object.values(OWNERSHIP_UNKNOWN_KINDS).flatMap((entry) => [entry.label, entry.meaning]),
  ...Object.values(FACT_FIELD_LABELS),
  WEAK_TIE_CAVEAT,
  RETROACTIVE_CAVEAT,
  DANGLING_CAVEAT,
  FINER_REFS_CAVEAT,
  DISPUTED_FACT_NOTE,
  RETROACTIVE_FACT_NOTE,
  MANPOWER_NO_DELTA_NOTE,
  REFUSED_OBSERVATION_NOTE,
  DISPUTE_SCAN_TRUNCATED_NOTE,
  SCORE_REFUSED_NOTE,
  CAUSALITY_STATEMENT,
];

/**
 * Word-boundary matching, not substring: banning the substring "late" would
 * catch "related" and "latest" and push the copy toward worse words to
 * satisfy the guard.
 */
export const findForbiddenWords = (text) => {
  if (typeof text !== 'string') return [];
  const hits = [];
  Object.values(FORBIDDEN_OUTCOME_WORDS).forEach((group) => {
    group.forEach((word) => {
      const pattern = new RegExp(`\\b${word.replace(/ /g, '\\s+')}\\b`, 'i');
      if (pattern.test(text)) hits.push(word);
    });
  });
  return hits;
};
